import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  useColorModeValue,
  Icon,
  VStack,
  Text,
  SimpleGrid,
  Card,
  CardBody,
  CardHeader,
  Avatar,
  Badge,
  HStack,
  Spinner,
  Alert,
  AlertIcon,
  Button,
  useToast,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useApi } from '@/contexts/ApiContext';
import type { CordeCommonModelsDtoUserprofileUserProfile } from '@/api/generated/v1/api';

// Icons for profile sections
const MyProfilesIcon = () => (
  <Icon viewBox="0 0 24 24" boxSize={5}>
    <path
      fill="currentColor"
      d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
    />
  </Icon>
);

const SearchProfilesIcon = () => (
  <Icon viewBox="0 0 24 24" boxSize={5}>
    <path
      fill="currentColor"
      d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"
    />
  </Icon>
);

const CreateProfileIcon = () => (
  <Icon viewBox="0 0 24 24" boxSize={5}>
    <path
      fill="currentColor"
      d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"
    />
  </Icon>
);

export const MyProfilesPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { UserprofileApi } = useApi();

  // Theme colors
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.600', 'gray.400');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  // State
  const [profiles, setProfiles] = useState<CordeCommonModelsDtoUserprofileUserProfile[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');

  // Load user profiles
  const loadProfiles = async () => {
    try {
      setIsLoading(true);
      setError('');

      const response = await UserprofileApi.listUserProfiles();
      const data = (response as any)?.data || response;
      setProfiles(data?.profiles || []);
    } catch (err: any) {
      console.error('Error loading profiles:', err);
      setError(err.message || t('profiles.failedToLoadProfiles'));
      toast({
        title: t('common.error'),
        description: err.message || t('profiles.failedToLoadProfiles'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle profile click
  const handleProfileClick = (profile: CordeCommonModelsDtoUserprofileUserProfile) => {
    navigate(`/profiles/${profile.id}`);
  };

  // Update sidebars with context actions and subsections
  useEffect(() => {
    // Load profiles on mount
    loadProfiles();

    // Update right sidebar with actions
    if (window.updateRightSidebar) {
      window.updateRightSidebar([
        {
          label: t('profiles.createProfile'),
          colorScheme: 'green',
          onClick: () => {
            navigate('/profile/create');
          },
        },
        {
          label: t('profiles.searchProfiles'),
          colorScheme: 'blue',
          variant: 'outline',
          onClick: () => {
            navigate('/profiles/search');
          },
        },
      ]);
    }

    // Update left sidebar with subsections info for profiles
    if (window.updateLeftSidebar) {
      const profileSections = [
        {
          path: '/profiles/my-profiles',
          label: t('profiles.myProfiles'),
          icon: MyProfilesIcon,
        },
        {
          path: '/profiles/search',
          label: t('profiles.searchProfiles'),
          icon: SearchProfilesIcon,
        },
      ];

      window.updateLeftSidebar({
        section: 'profiles',
        subsections: profileSections
      });
    }

    return () => {
      if (window.updateRightSidebar) {
        window.updateRightSidebar([]);
      }
      // Don't clear left sidebar subsections - let them persist for navigation
    };
  }, [t, navigate]);

  // Loading state
  if (isLoading && profiles.length === 0) {
    return (
      <Box minH="100vh" bg={bgColor}>
        <Container maxW="7xl" py={6}>
          <VStack spacing={4} py={8}>
            <Spinner size="lg" />
            <Text>{t('profiles.loadingProfiles')}</Text>
          </VStack>
        </Container>
      </Box>
    );
  }

  // Error state
  if (error && profiles.length === 0) {
    return (
      <Box minH="100vh" bg={bgColor}>
        <Container maxW="7xl" py={6}>
          <Box textAlign="center" py={8}>
            <Alert status="error" mb={4}>
              <AlertIcon />
              {error}
            </Alert>
            <Button onClick={loadProfiles} colorScheme="primary">
              {t('common.retry')}
            </Button>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg={bgColor}>
      <Container maxW="7xl" py={6}>
        <VStack spacing={6} align="stretch">
          {/* Header */}
          <Text fontSize="xl" fontWeight="semibold" color="text.primary">
            {t('profiles.myProfiles')}
          </Text>

          {/* Results summary */}
          <HStack justify="space-between" align="center">
            <Text fontSize="sm" color={textColor}>
              {t('profiles.totalProfiles')}: {profiles.length}
            </Text>
            {isLoading && <Spinner size="sm" />}
          </HStack>

          {/* Profiles grid */}
          {profiles.length === 0 && !isLoading ? (
            <Box textAlign="center" py={12}>
              <Icon viewBox="0 0 24 24" boxSize={12} color="gray.400">
                <path
                  fill="currentColor"
                  d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
                />
              </Icon>
              <Text fontSize="lg" fontWeight="medium" mt={4} mb={2}>
                {t('profiles.noProfilesFound')}
              </Text>
              <Text color={textColor}>
                {t('profiles.myProfilesDescription')}
              </Text>
            </Box>
          ) : (
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
              {profiles.map((profile) => (
                <Card
                  key={profile.id}
                  bg={cardBg}
                  border="1px"
                  borderColor={borderColor}
                  cursor="pointer"
                  transition="all 0.2s"
                  _hover={{
                    borderColor: 'primary.400',
                    bg: hoverBg,
                    transform: 'translateY(-2px)',
                    shadow: 'md',
                  }}
                  onClick={() => handleProfileClick(profile)}
                >
                  <CardHeader pb={3}>
                    <HStack spacing={3}>
                      <Avatar
                        size="md"
                        name={profile.display_name || profile.username}
                        src={profile.avatar_url}
                        bg="primary.500"
                        color="white"
                      />
                      <VStack align="start" spacing={1} flex={1} minW={0}>
                        <Text
                          fontWeight="semibold"
                          fontSize="md"
                          color="text.primary"
                          noOfLines={1}
                        >
                          {profile.display_name || profile.username}
                        </Text>
                        <Text
                          fontSize="sm"
                          color={textColor}
                          noOfLines={1}
                        >
                          @{profile.username}
                        </Text>
                        {profile.profile_type && (
                          <Badge
                            colorScheme="blue"
                            variant="subtle"
                            size="sm"
                          >
                            {profile.profile_type}
                          </Badge>
                        )}
                      </VStack>
                    </HStack>
                  </CardHeader>

                  <CardBody pt={0}>
                    <VStack align="stretch" spacing={3}>
                      {profile.bio && (
                        <Text
                          color={textColor}
                          fontSize="sm"
                          noOfLines={3}
                          lineHeight="1.4"
                        >
                          {profile.bio}
                        </Text>
                      )}

                      <HStack justify="space-between" fontSize="xs" color={textColor}>
                        <Text>
                          {t('profiles.created')}: {profile.created_at ? new Date(profile.created_at).toLocaleDateString() : '-'}
                        </Text>
                        {profile.location && (
                          <Text>
                            📍 {profile.location}
                          </Text>
                        )}
                      </HStack>
                    </VStack>
                  </CardBody>
                </Card>
              ))}
            </SimpleGrid>
          )}
        </VStack>
      </Container>
    </Box>
  );
};